import { useRef, useEffect } from 'react'
import { X } from 'lucide-react'
import type { QuestionResponseDetailResponse } from '../types'

interface ReviewQuestionPickerProps {
  responses: QuestionResponseDetailResponse[]
  currentIndex: number
  onSelect: (index: number) => void
  onClose: () => void
}

export default function ReviewQuestionPicker({
  responses,
  currentIndex,
  onSelect,
  onClose,
}: ReviewQuestionPickerProps) {
  const pickerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onClose])

  const getButtonColor = (response: QuestionResponseDetailResponse, index: number) => {
    const isCurrent = index === currentIndex

    if (response.isAttempted && response.isCorrect) {
      return isCurrent
        ? 'bg-green-500 text-white ring-2 ring-blue-500 ring-offset-2'
        : 'bg-green-500 text-white'
    }
    if (response.isAttempted && response.isCorrect === false) {
      return isCurrent
        ? 'bg-red-500 text-white ring-2 ring-blue-500 ring-offset-2'
        : 'bg-red-500 text-white'
    }
    // Unanswered (SKIPPED/UNSEEN/SEEN)
    return isCurrent
      ? 'bg-gray-200 text-gray-700 ring-2 ring-blue-500 ring-offset-2'
      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-30 p-4">
      <div
        ref={pickerRef}
        className="bg-white rounded-xl shadow-xl max-w-sm w-full max-h-[70vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Select Question</h3>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Question Grid */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="grid grid-cols-5 gap-2">
            {responses.map((response, i) => (
              <button
                key={response.responseId}
                onClick={() => {
                  onSelect(i)
                  onClose()
                }}
                className={`w-full aspect-square rounded-lg text-sm font-medium transition-colors ${getButtonColor(response, i)}`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        </div>

        {/* Legend */}
        <div className="px-4 py-3 border-t border-gray-200">
          <div className="flex items-center justify-center gap-4 text-xs text-gray-600">
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm bg-green-500" />
              <span>Correct</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm bg-red-500" />
              <span>Incorrect</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm bg-gray-200" />
              <span>Unanswered</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
